/** @jsx jsx */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { jsx, SerializedStyles } from '@emotion/core';
import { CircularProgress } from '@material-ui/core';
import { Theme } from 'features/Theme';

interface PropTypes {
  src: string;
  alt: string;
  imgCss?: (theme: Theme) => SerializedStyles;
  spinnerCss?: (theme: Theme) => SerializedStyles;
}

type ImageLoadedHook = (
  src: string,
) => [React.MutableRefObject<HTMLImageElement | null>, boolean, () => void];

const useImageLoaded: ImageLoadedHook = (src: string) => {
  const [loaded, setLoaded] = useState(false);
  const ref = useRef<HTMLImageElement | null>(null);

  const onLoad = useCallback(() => {
    setLoaded(true);
  }, [setLoaded]);

  useEffect(() => {
    if (ref.current && ref.current.complete) {
      setLoaded(true);
    } else {
      setLoaded(false);
    }
  }, [src]);

  return [ref, loaded, onLoad];
};

const ImageWithLoadSpinner = (props: PropTypes) => {
  const { src, alt, imgCss, spinnerCss } = props;
  const [ref, loaded, onLoad] = useImageLoaded(src);
  return (
    <React.Fragment>
      <img
        ref={ref}
        src={src}
        alt={alt}
        css={imgCss}
        style={{ display: loaded ? 'block' : 'none' }}
        onLoad={onLoad}
      />
      <CircularProgress
        aria-label={`circular-progress-for-${alt}`}
        size={24}
        css={spinnerCss}
        style={{ display: loaded ? 'none' : 'block' }}
      />
    </React.Fragment>
  );
};

export default ImageWithLoadSpinner;
